import React, { memo, useState, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { UnifiedSidebar } from "./UnifiedSidebar";
import { FloatingChatList } from "@/components/chat/FloatingChatList";
import { MobileHeaderBar } from "./MobileHeaderBar";
import { UnifiedLayoutSkeleton } from "./UnifiedLayoutSkeleton";
import { useLocation } from "react-router-dom";
import { useCompanies } from "@/hooks/auth";
import { useIsMobile, useSidebarResponsive } from "@/hooks/use-mobile";
import { useWorkspacePersistence } from "@/hooks/workspace/useWorkspacePersistence";
import { cn } from "@/lib/utils";


interface UnifiedLayoutProps {
  children: React.ReactNode;
}

export const UnifiedLayout = memo(({ children }: UnifiedLayoutProps) => {
  const isMobile = useIsMobile();
  const shouldCollapseSidebar = useSidebarResponsive();
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(!shouldCollapseSidebar);
  
  const location = useLocation();
  
  // Detect Evee AI pages to remove top padding
  const isEveeAIPage = location.pathname === '/evee-ai' || location.pathname === '/evee-ai-agent';
  
  // Track sidebar readiness (company data loaded)
  const { isCompanyReady } = useCompanies();
  // Initialize based on current isCompanyReady to handle remounts gracefully
  const [isSidebarReady, setIsSidebarReady] = useState(isCompanyReady);

  // Mobile header only shows on mobile (<768px), tablet and desktop use left sidebar
  const showMobileHeader = isMobile;

  // Debug logging
  useEffect(() => {
    console.log('🔍 [UnifiedLayout] Breakpoint Debug:', {
      windowWidth: window.innerWidth,
      shouldCollapseSidebar,
      isSidebarOpen,
      isMobile,
      showMobileHeader
    });
  }, [shouldCollapseSidebar, isSidebarOpen, isMobile, showMobileHeader]);

  // Update sidebar state when breakpoint changes
  useEffect(() => {
    setIsSidebarOpen(!shouldCollapseSidebar);
  }, [shouldCollapseSidebar]);
  
  // Automatically persist workspace mode based on current route
  useWorkspacePersistence();
  
  // Track when sidebar is ready (company data loaded and initialized)
  useEffect(() => {
    if (isCompanyReady && !isSidebarReady) {
      // Small delay to ensure smooth transition, but only if not already ready
      const timer = setTimeout(() => {
        console.log('[UnifiedLayout] Setting sidebar ready from isCompanyReady');
        setIsSidebarReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCompanyReady, isSidebarReady]);
  
  // Safety timeout: force sidebar ready after 2 seconds to prevent infinite loading
  useEffect(() => {
    const maxWaitTimer = setTimeout(() => {
      if (!isSidebarReady) {
        console.warn('[UnifiedLayout] Forcing sidebar ready after 2s timeout');
        setIsSidebarReady(true);
      }
    }, 2000);
    return () => clearTimeout(maxWaitTimer);
  }, [isSidebarReady]);
  
  // Delay showing FloatingChatList until layout is ready to prevent premature loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLayoutReady(true);
    }, 100); // Small delay to ensure skeleton shows first
    
    return () => clearTimeout(timer);
  }, []);
  
  // Main container classes
  // All pages get consistent top padding: mobile clears header, desktop gets breathing room
  // Exception: Evee AI pages extend to top edge
  const mainClasses = cn(
    'flex-1 overflow-y-auto overflow-x-hidden min-h-0',
    showMobileHeader && !isEveeAIPage ? 'pt-20' : '', // Mobile: 80px header clearance (not for Evee AI)
    !showMobileHeader && !isEveeAIPage ? 'pt-6 lg:pt-8' : '' // Desktop: breathing room (not for Evee AI)
  );
  
  // Show skeleton until sidebar is ready
  if (!isSidebarReady) {
    return <UnifiedLayoutSkeleton />;
  }
  
  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className={cn(
        "h-svh max-h-svh flex flex-col w-full overflow-hidden",
        isEveeAIPage ? "bg-background" : "bg-transparent"
      )}>
        {/* Mobile Header Bar - Only on mobile (<768px), tablet/desktop use left sidebar */}
        {showMobileHeader && isSidebarReady && <MobileHeaderBar />}
        
        <div className="flex flex-1 overflow-hidden min-h-0">
          <UnifiedSidebar />
          <main className={mainClasses}>
            {children}
          </main>
        </div>
        
        {/* Floating Chat List - Show only after layout is ready */}
        {isLayoutReady && <FloatingChatList />}
      </div>
    </SidebarProvider>
  );
});